import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

// Type definitions
interface FemaError {
  id: string;
  timestamp: number;
  category: string;
  severity: 'error' | 'warning' | 'info';
  message: string;
  stack?: string;
  context: Record<string, any>;
  fingerprint: string;
  reportAttempts: number;
  reported: boolean;
}

interface FemaConfig {
  enabled: boolean;
  maxErrors: number;
  reportingInterval: number;
  batchSize: number;
  rateLimitWindow: number;
  maxReportsPerWindow: number;
}

interface FemaMetrics {
  totalErrors: number;
  totalReports: number;
  lastErrorTime: number | null;
  categoryCounts: Record<string, number>;
  severityCounts: Record<string, number>;
}

interface FemaStore {
  errors: FemaError[];
  errorCounts: Record<string, number>;
  isReporting: boolean;
  reportingQueue: FemaError[];
  lastReportTime: number | null;
  config: FemaConfig;
  metrics: FemaMetrics;

  addError: (error: Partial<FemaError>) => string;
  addToReportingQueue: (error: FemaError) => void;
  processReportingQueue: () => Promise<void>;
  clearErrors: () => void;
  removeError: (errorId: string) => void;
  updateConfig: (newConfig: Partial<FemaConfig>) => void;
  getErrorStats: () => {
    total: number;
    recent: number;
    daily: number;
    byCategory: Record<string, number>;
    bySeverity: Record<string, number>;
    queueSize: number;
    isReporting: boolean;
  };
  getTopErrors: (limit?: number) => any[];
}

// FEMA (Failure & Error Monitoring & Analysis) Store
// Manages error tracking, classification, and reporting state

const createFemaStore = (set: any, get: any): FemaStore => ({
  // Error tracking state
  errors: [],
  errorCounts: {},
  isReporting: false,
  reportingQueue: [],
  lastReportTime: null,
  
  // Configuration state
  config: {
    enabled: true,
    maxErrors: 100, // Max errors to keep in memory
    reportingInterval: 5000, // 5 seconds
    batchSize: 10, // Max errors per batch
    rateLimitWindow: 60000, // 1 minute
    maxReportsPerWindow: 20,
  },
  
  // Metrics state
  metrics: {
    totalErrors: 0,
    totalReports: 0,
    lastErrorTime: null,
    categoryCounts: {},
    severityCounts: {},
  },
  
  // Actions
  addError: (error) => {
    const state = get();
    const errorId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const errorEntry = {
      id: errorId,
      timestamp: Date.now(),
      category: error.category || 'unknown',
      severity: error.severity || 'error',
      message: error.message || 'Unknown error',
      stack: error.stack,
      context: error.context || {},
      fingerprint: error.fingerprint || generateFingerprint(error),
      reportAttempts: 0,
      reported: false,
    };
    
    // Update error counts for rate limiting
    const fingerprint = errorEntry.fingerprint;
    const currentCount = state.errorCounts[fingerprint] || 0;
    
    set((state: FemaStore) => {
      // Limit the number of errors in memory
      let newErrors = [...state.errors, errorEntry];
      if (newErrors.length > state.config.maxErrors) {
        newErrors = newErrors.slice(-state.config.maxErrors);
      }
      
      // Update metrics
      const newCategoryCounts = { ...state.metrics.categoryCounts };
      const newSeverityCounts = { ...state.metrics.severityCounts };
      
      newCategoryCounts[errorEntry.category] = (newCategoryCounts[errorEntry.category] || 0) + 1;
      newSeverityCounts[errorEntry.severity] = (newSeverityCounts[errorEntry.severity] || 0) + 1;
      
      return {
        errors: newErrors,
        errorCounts: {
          ...state.errorCounts,
          [fingerprint]: currentCount + 1,
        },
        metrics: {
          ...state.metrics,
          totalErrors: state.metrics.totalErrors + 1,
          lastErrorTime: errorEntry.timestamp,
          categoryCounts: newCategoryCounts,
          severityCounts: newSeverityCounts,
        },
      };
    }, false, 'fema/addError');
    
    // Add to reporting queue if not already reported
    if (!errorEntry.reported) {
      get().addToReportingQueue(errorEntry);
    }
    
    return errorId;
  },
  
  addToReportingQueue: (error) => {
    set((state: FemaStore) => ({
      reportingQueue: [...state.reportingQueue, error],
    }), false, 'fema/addToReportingQueue');
    
    // Trigger reporting if not already in progress
    if (!get().isReporting) {
      get().processReportingQueue();
    }
  },
  
  processReportingQueue: async () => {
    const state = get();
    
    if (!state.config.enabled || state.isReporting || state.reportingQueue.length === 0) {
      return;
    }
    
    set({ isReporting: true }, false, 'fema/processReportingQueue/start');
    
    try {
      const now = Date.now();
      const { rateLimitWindow, maxReportsPerWindow } = state.config;
      
      // Check rate limiting
      const recentReports = state.metrics.totalReports;
      const timeSinceLastReport = state.lastReportTime ? now - state.lastReportTime : rateLimitWindow;
      
      if (timeSinceLastReport < rateLimitWindow && recentReports >= maxReportsPerWindow) {
        console.warn('FEMA: Rate limit reached, skipping error reporting');
        set({ isReporting: false }, false, 'fema/processReportingQueue/rateLimit');
        return;
      }
      
      // Process batch
      const batchSize = Math.min(state.config.batchSize, state.reportingQueue.length);
      const batch = state.reportingQueue.slice(0, batchSize);
      
      // Simulate reporting (in real implementation, this would call an API)
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Mark errors as reported
      const reportedIds = batch.map((error: FemaError) => error.id);
      
      set((state: FemaStore) => ({
        errors: state.errors.map((error: FemaError) =>
          reportedIds.includes(error.id)
            ? { ...error, reported: true, reportAttempts: error.reportAttempts + 1 }
            : error
        ),
        reportingQueue: state.reportingQueue.slice(batchSize),
        lastReportTime: now,
        metrics: {
          ...state.metrics,
          totalReports: state.metrics.totalReports + batch.length,
        },
        isReporting: false,
      }), false, 'fema/processReportingQueue/success');
      
      // Continue processing if more items in queue
      if (get().reportingQueue.length > 0) {
        setTimeout(() => get().processReportingQueue(), state.config.reportingInterval);
      }
      
    } catch (error) {
      console.error('FEMA: Error processing reporting queue:', error);
      set({ isReporting: false }, false, 'fema/processReportingQueue/error');
    }
  },
  
  clearErrors: () => {
    set({
      errors: [],
      errorCounts: {},
      reportingQueue: [],
    }, false, 'fema/clearErrors');
  },
  
  removeError: (errorId) => {
    set((state: FemaStore) => ({
      errors: state.errors.filter((error: FemaError) => error.id !== errorId),
      reportingQueue: state.reportingQueue.filter((error: FemaError) => error.id !== errorId),
    }), false, 'fema/removeError');
  },
  
  updateConfig: (newConfig) => {
    set((state: FemaStore) => ({
      config: { ...state.config, ...newConfig },
    }), false, 'fema/updateConfig');
  },
  
  getErrorStats: () => {
    const state = get();
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;
    const oneDay = 24 * oneHour;
    
    const recentErrors = state.errors.filter((error: FemaError) => now - error.timestamp < oneHour);
    const dailyErrors = state.errors.filter((error: FemaError) => now - error.timestamp < oneDay);
    
    return {
      total: state.errors.length,
      recent: recentErrors.length,
      daily: dailyErrors.length,
      byCategory: state.metrics.categoryCounts,
      bySeverity: state.metrics.severityCounts,
      queueSize: state.reportingQueue.length,
      isReporting: state.isReporting,
    };
  },
  
  getTopErrors: (limit = 10) => {
    const state = get();
    const errorsByFingerprint: Record<string, FemaError & { count: number; firstSeen: number; lastSeen: number }> = {};

    // Group errors by fingerprint and count occurrences
    state.errors.forEach((error: FemaError) => {
      if (!errorsByFingerprint[error.fingerprint]) {
        errorsByFingerprint[error.fingerprint] = {
          ...error,
          count: 0,
          firstSeen: error.timestamp,
          lastSeen: error.timestamp,
        };
      }

      const existing = errorsByFingerprint[error.fingerprint];
      if (existing) {
        existing.count++;
        existing.lastSeen = Math.max(existing.lastSeen, error.timestamp);
        existing.firstSeen = Math.min(existing.firstSeen, error.timestamp);
      }
    });

    return Object.values(errorsByFingerprint)
      .sort((a: any, b: any) => b.count - a.count)
      .slice(0, limit);
  },
});

// Helper function to generate error fingerprints
const generateFingerprint = (error: any): string => {
  const message = error.message || '';
  const category = error.category || 'unknown';
  const severity = error.severity || 'error';
  
  // Create a simple hash from error characteristics
  const combined = `${category}:${severity}:${message}`;
  let hash = 0;
  
  for (let i = 0; i < combined.length; i++) {
    const char = combined.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash).toString(36);
};

// Create the FEMA store
export const useFemaStore = create<FemaStore>()(
  devtools(
    createFemaStore,
    {
      name: 'FEMA Store',
    }
  )
);

// Selectors for specific parts of FEMA state
export const useFemaErrors = () => useFemaStore((state) => ({
  errors: state.errors,
  errorCounts: state.errorCounts,
  addError: state.addError,
  removeError: state.removeError,
  clearErrors: state.clearErrors,
}));

export const useFemaMetrics = () => useFemaStore((state) => ({
  metrics: state.metrics,
  getErrorStats: state.getErrorStats,
  getTopErrors: state.getTopErrors,
}));

export const useFemaConfig = () => useFemaStore((state) => ({
  config: state.config,
  updateConfig: state.updateConfig,
}));

export const useFemaReporting = () => useFemaStore((state) => ({
  isReporting: state.isReporting,
  reportingQueue: state.reportingQueue,
  processReportingQueue: state.processReportingQueue,
}));

export default useFemaStore;
